import React, { useState, useEffect } from 'react';
import { Card, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { PhoneInput } from '../components/ui/PhoneInput';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { canManageOwnBilling, isAdmin } from '../utils/roles';
import { apiUpdate } from '../api/apiClient';
import { stripeApi } from '../api/stripe';

export const Settings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'account' | 'billing'>(() =>
    new URLSearchParams(window.location.search).get('tab') === 'billing' ? 'billing' : 'account'
  );
  const showBilling = canManageOwnBilling((user?.role || 'viewer') as any);
  const visibleTab = activeTab === 'billing' && showBilling ? 'billing' : 'account';

  // Profile fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Admin / application fields
  const [timezone, setTimezone] = useState('America/New_York');

  // 2FA toggle — stored on the user record
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);

  // Feedback state
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileStatus, setProfileStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminStatus, setAdminStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Load user data from auth context on mount / when user changes
  useEffect(() => {
    if (user) {
      setName(user.name ?? [user.first_name, user.last_name].filter(Boolean).join(' '));
      setEmail(user.email ?? '');
      setPhone(user.phone ?? '');
      setTimezone((user as any).timezone ?? 'America/New_York');
      if ((user as any).two_factor_enabled != null) setTwoFaEnabled(!!(user as any).two_factor_enabled);
    }
  }, [user]);

  const saveProfile = async () => {
    if (!user) return;
    setProfileSaving(true);
    setProfileStatus(null);
    try {
      const parts = name.trim().split(/\s+/);
      const payload: Record<string, any> = {
        first_name: parts.shift() || '',
        last_name: parts.join(' '),
        email,
        phone,
      };

      await apiUpdate('users', user.uuid || user.id, payload);
      updateUser({ name, first_name: payload.first_name, last_name: payload.last_name, email, phone });
      setProfileStatus({ ok: true, msg: 'Profile saved successfully!' });
    } catch (err: any) {
      setProfileStatus({ ok: false, msg: err?.message ?? 'Failed to save profile.' });
    } finally {
      setProfileSaving(false);
    }
  };

  const saveAdminSettings = async () => {
    if (!user) return;
    setAdminSaving(true);
    setAdminStatus(null);
    try {
      await apiUpdate('users', (user as any).uuid || user.id, {
        two_factor_enabled: twoFaEnabled,
        timezone,
      });
      updateUser({ two_factor_enabled: twoFaEnabled, timezone });
      setAdminStatus({ ok: true, msg: 'Settings saved successfully!' });
    } catch (err: any) {
      setAdminStatus({ ok: false, msg: err?.message ?? 'Failed to save settings.' });
    } finally {
      setAdminSaving(false);
    }
  };

  const selectTab = (tab: 'account' | 'billing') => {
    setActiveTab(tab);
    const next = new URL(window.location.href);
    if (tab === 'billing') next.searchParams.set('tab', 'billing');
    else next.searchParams.delete('tab');
    window.history.replaceState({}, '', `${next.pathname}${next.search}${next.hash}`);
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    setPortalError(null);
    try {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('tab', 'billing');
      // The server resolves the Stripe customer from the signed-in user's
      // customer/dealer scope. Never send a browser-selected cus_ identifier.
      const { url } = await stripeApi.createBillingPortal('', returnUrl.toString());
      window.location.assign(url);
    } catch (err: any) {
      setPortalError(err?.message || 'The billing portal could not be opened.');
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-gray-500 mt-1">Manage your account and application settings</p>
      </div>

      {showBilling && (
        <div className="mb-6 border-b border-gray-200" role="tablist" aria-label="Account settings sections">
          {([
            ['account', 'Account Settings'],
            ['billing', 'Billing Settings'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={visibleTab === key}
              onClick={() => selectTab(key)}
              className={`mr-6 border-b-2 px-1 py-3 text-sm font-semibold transition-colors ${visibleTab === key ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {visibleTab === 'account' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Account / Profile ── */}
        <Card>
          <CardHeader title="Account Settings" subtitle="Update your personal information" />

          {profileStatus && (
            <div className={`text-sm p-3 rounded-lg mb-4 ${profileStatus.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {profileStatus.msg}
            </div>
          )}

          <Input label="Full Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <PhoneInput label="Phone" value={phone} onChange={setPhone} />

          <Button onClick={saveProfile} disabled={profileSaving}>
            {profileSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </Card>

        {/* ── Admin / Application Settings ── */}
        {isAdmin(user?.role || 'customer') && (
          <Card>
            <CardHeader title="Application Settings" subtitle="Admin-only configuration" />

            {adminStatus && (
              <div className={`text-sm p-3 rounded-lg mb-4 ${adminStatus.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {adminStatus.msg}
              </div>
            )}

            <Input label="Default Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Two-Factor Authentication</label>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Require 2FA for all admin users</span>
                <button
                  type="button"
                  onClick={() => setTwoFaEnabled((v) => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${twoFaEnabled ? 'bg-brand-600' : 'bg-gray-300'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${twoFaEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>
            </div>

            <Button onClick={saveAdminSettings} disabled={adminSaving}>
              {adminSaving ? 'Saving…' : 'Save Settings'}
            </Button>
          </Card>
        )}
      </div>}

      {visibleTab === 'billing' && showBilling && (
        <Card className="max-w-3xl">
          <CardHeader
            title="Billing Settings"
            subtitle="Securely manage this account through Stripe"
          />
          <p className="mb-6 max-w-2xl text-sm leading-6 text-gray-600">
            Open the Stripe Billing Portal to view invoices, update payment methods, and manage subscriptions for your customer account.
          </p>
          {portalError && (
            <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {portalError}
            </div>
          )}
          <Button onClick={openBillingPortal} disabled={portalLoading}>
            {portalLoading ? 'Opening Stripe…' : 'Open Stripe Billing Portal'}
          </Button>
          <p className="mt-4 text-xs text-gray-500">
            You will return to Intrlock Account Settings when you finish.
          </p>
        </Card>
      )}
    </div>
  );
};
